'use client'

import * as React from 'react'
import { useState } from 'react'
import { PropertyBadge } from './property-badge'
import {
  TrashIcon,
  PlusIcon,
  CircleNotchIcon,
  XIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react'
import type { Icon } from '@phosphor-icons/react'
import { ICON_SIZES, MOBILE_OVERFLOW, FOCUS_STYLES } from '@/ui/design-tokens'
import { Input } from '@/ui/input'
import { ResponsiveSelector } from '@/ui/responsive-selector'
import { cn } from '@/lib/tailwind'
import type { SelectableEntity } from './property-types'

interface MultiSelectPropertyProps<T extends SelectableEntity> {
  /** Display label */
  label: string
  /** Icon to show in badges */
  Icon: Icon
  /** Currently selected items */
  selectedItems: T[]
  /** All available items to choose from */
  availableItems: T[]
  /** Called when an item is added */
  onAdd: (item: T) => void
  /** Called when an item is removed */
  onRemove: (item: T) => void
  /** Function to get display label for an item */
  getItemLabel: (item: T) => string
  /** Whether the property is disabled */
  disabled?: boolean
  /** Whether items are currently loading */
  isLoading?: boolean
  /** Optional callback to create a new item - shows "Add new..." option */
  onAddNew?: () => void
  /** Label for the "add new" option (default: "Add new...") */
  addNewLabel?: string
}

/**
 * A property field that allows selecting multiple items from a list.
 * Shows selected items as badges with a remove option, plus an "add more"
 * selector when there are unselected items available.
 * Uses ResponsiveSelector: popover on desktop, bottom sheet on mobile.
 */
export function MultiSelectProperty<T extends SelectableEntity>({
  label,
  Icon: IconComponent,
  selectedItems,
  availableItems,
  onAdd,
  onRemove,
  getItemLabel,
  disabled = false,
  isLoading = false,
  onAddNew,
  addNewLabel = 'Add new...',
}: MultiSelectPropertyProps<T>) {
  const [addOpen, setAddOpen] = useState(false)
  const [removeOpen, setRemoveOpen] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  // Calculate unselected items
  const unselectedItems = availableItems.filter(
    (item) => !selectedItems.some((selected) => selected.id === item.id),
  )

  const showSearch = availableItems.length > 5
  const lowerSearch = search.toLowerCase()
  const filteredUnselected = search
    ? unselectedItems.filter((item) =>
        getItemLabel(item).toLowerCase().includes(lowerSearch),
      )
    : unselectedItems

  const hasSelectedItems = selectedItems.length > 0
  const hasUnselectedItems = unselectedItems.length > 0
  const hasNoAvailableItems = availableItems.length === 0

  // Show loading badge while items are being fetched
  if (isLoading && hasNoAvailableItems && !hasSelectedItems) {
    return (
      <PropertyBadge
        Icon={CircleNotchIcon}
        label={`Loading ${label.toLowerCase()}...`}
        active={false}
        disabled={true}
        className="animate-pulse"
      />
    )
  }

  // Empty state: no available items, nothing selected, not loading
  if (hasNoAvailableItems && !hasSelectedItems && !isLoading) {
    if (onAddNew) {
      // Show "Add new" CTA when onAddNew is provided
      return (
        <PropertyBadge
          Icon={PlusIcon}
          label={addNewLabel}
          onClick={() => onAddNew()}
          active={false}
        />
      )
    }
    // Show disabled "No items available" message
    return (
      <PropertyBadge
        Icon={IconComponent}
        label={`No ${label.toLowerCase()} available`}
        active={false}
        disabled={true}
      />
    )
  }

  const handleAddItem = (item: T) => {
    onAdd(item)
    setAddOpen(false)
  }

  const handleRemoveItem = (item: T) => {
    onRemove(item)
    setRemoveOpen(null)
  }

  // Calculate overflow for mobile (show max 2 items, rest as "+N more")
  const mobileMaxVisible = MOBILE_OVERFLOW.maxVisible
  const hasOverflow = selectedItems.length > mobileMaxVisible
  const overflowCount = selectedItems.length - mobileMaxVisible
  const hiddenOnMobile = selectedItems.slice(mobileMaxVisible)

  return (
    <>
      {/* Selected items as badges */}
      {selectedItems.map((item, index) => (
        <ResponsiveSelector
          key={item.id}
          open={removeOpen === item.id}
          onOpenChange={(open) => setRemoveOpen(open ? item.id : null)}
          ariaLabel={`Options for ${getItemLabel(item)}`}
          trigger={
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-sm transition-colors',
                'text-foreground hover:bg-muted/30',
                FOCUS_STYLES.ringCompact,
                'max-w-[120px] sm:max-w-[140px]',
                index >= mobileMaxVisible && 'hidden sm:inline-flex',
                'active:scale-[0.98]',
              )}
            >
              <IconComponent
                className={`${ICON_SIZES.compact} shrink-0`}
                weight="bold"
              />
              <span className="truncate">{getItemLabel(item)}</span>
            </button>
          }
        >
          <div className="space-y-0.5">
            <div className="flex min-h-[44px] w-full items-center rounded-sm px-2 py-1.5 text-sm sm:min-h-[32px]">
              {getItemLabel(item)}
            </div>
            <div className="bg-border my-1 h-px" />
            <button
              type="button"
              onClick={() => handleRemoveItem(item)}
              className="text-destructive hover:bg-muted flex min-h-[44px] w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]"
            >
              <TrashIcon className={ICON_SIZES.compact} />
              Remove
            </button>
          </div>
        </ResponsiveSelector>
      ))}

      {/* Mobile overflow badge - shows "+N more" when items are hidden */}
      {hasOverflow && (
        <ResponsiveSelector
          open={removeOpen === '__overflow__'}
          onOpenChange={(open) => setRemoveOpen(open ? '__overflow__' : null)}
          ariaLabel={`${overflowCount} more items`}
          trigger={
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'text-foreground inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-sm transition-colors',
                'hover:bg-muted/30',
                FOCUS_STYLES.ringCompact,
                'sm:hidden',
                'active:scale-[0.98]',
              )}
            >
              <span className="text-sm">+{overflowCount}</span>
            </button>
          }
        >
          <div className="space-y-0.5">
            {hiddenOnMobile.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleRemoveItem(item)}
                className="hover:bg-muted flex min-h-[44px] w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]"
              >
                <XIcon className={ICON_SIZES.compact} />
                {getItemLabel(item)}
              </button>
            ))}
          </div>
        </ResponsiveSelector>
      )}

      {/* Add selector - visible when there are unselected items or onAddNew is provided */}
      {(hasUnselectedItems || onAddNew) && (
        <ResponsiveSelector
          open={addOpen}
          onOpenChange={(open) => {
            setAddOpen(open)
            if (!open) setSearch('')
          }}
          ariaLabel={`Add ${label.toLowerCase()}`}
          trigger={
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-sm transition-colors',
                'text-muted-foreground hover:text-foreground hover:bg-muted/30',
                FOCUS_STYLES.ringCompact,
                'active:scale-[0.98]',
              )}
            >
              {hasSelectedItems ? (
                <PlusIcon className={`${ICON_SIZES.compact} shrink-0`} />
              ) : (
                <IconComponent className={`${ICON_SIZES.compact} shrink-0`} />
              )}
              <span>
                {hasSelectedItems ? `Add ${label.toLowerCase()}` : label}
              </span>
            </button>
          }
        >
          <div>
            {showSearch && (
              <div className="relative mb-2">
                <MagnifyingGlassIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 h-3 w-3 -translate-y-1/2" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 pr-2 pl-7 text-sm"
                />
              </div>
            )}
            <div className="max-h-48 space-y-0.5 overflow-y-auto">
              {filteredUnselected.length === 0 ? (
                <p className="text-muted-foreground py-4 text-center text-sm">
                  No matches
                </p>
              ) : (
                filteredUnselected.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleAddItem(item)}
                    className="hover:bg-muted flex min-h-[44px] w-full items-center rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]"
                  >
                    {getItemLabel(item)}
                  </button>
                ))
              )}
            </div>
            {/* Fixed footer — always visible, outside scroll area */}
            {onAddNew && !search && (
              <>
                {hasUnselectedItems && <div className="bg-border my-1 h-px" />}
                <button
                  type="button"
                  onClick={() => {
                    setAddOpen(false)
                    onAddNew()
                  }}
                  className="text-primary hover:bg-muted flex min-h-[44px] w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors sm:min-h-[32px]"
                >
                  <PlusIcon className={ICON_SIZES.compact} />
                  {addNewLabel}
                </button>
              </>
            )}
          </div>
        </ResponsiveSelector>
      )}
    </>
  )
}
